/**
 * WorkDetailsCard Component
 * Reusable card showing work summary: code, name, status, type, category, UDA, scheme, sanction amount
 */

import ContractorLink from '../ContractorLink';

const fmtCurrency = (n: number) => {
  const cr = (Number(n) || 0) / 10000000;
  return `${cr.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Cr`;
};

// Adaptive: amounts of 1 lakh or more in crores, smaller amounts in full rupees
// (so small test amounts like ₹7,000 don't collapse to "₹0.00 Cr").
const fmtAmount = (n: number) => {
  const v = Number(n) || 0;
  return v >= 100000 ? `${fmtCurrency(v)}` : v.toLocaleString('en-IN');
};

interface WorkDetailsCardProps {
  workCode?: string;
  workName?: string;
  workStatus?: string;
  workType?: string;
  workCategory?: string;
  udaName?: string;
  schemeName?: string;
  sanctionAmount?: number;
  /** Sum of the General Abstract (BOQ) line-item amounts. Shown next to Total
   *  Project Cost; rendered in red when it differs from sanctionAmount. */
  abstractAmount?: number;
  /** Extra fields to show in the bottom row */
  extraFields?: { label: string; value: string }[];
  className?: string;
  /** Called when work code is clicked */
  onWorkCodeClick?: () => void;
  /** Contractor name — if set, shown as clickable link */
  contractorName?: string;
  /** Contractor id — lets the name open the Contractor Profile without onContractorClick */
  contractorId?: number | null;
  /** Called when contractor name is clicked (to open profile) */
  onContractorClick?: () => void;
  /** Called when "Not Assigned" is clicked (to open contractor selector) */
  onAssignContractor?: () => void;
  /** Compact single-row layout — fields sit inline right after the code/status
   *  instead of in a field grid. Use in tight panels (e.g. the Work Schedule
   *  slide-over) where the content below needs the vertical room. */
  dense?: boolean;
}

export default function WorkDetailsCard({
  workCode, workName, workStatus, workType, workCategory,
  udaName, schemeName, sanctionAmount, abstractAmount, extraFields, className = '', onWorkCodeClick,
  contractorName, contractorId, onContractorClick, onAssignContractor, dense = false,
}: WorkDetailsCardProps) {
  const showAbstract = abstractAmount != null;
  const abstractMatches = showAbstract && Math.abs((Number(abstractAmount) || 0) - (Number(sanctionAmount) || 0)) < 1;

  // Compact layout: identity on line 1, all fields inline on line 2 — two short
  // rows, so the card stays far shorter than the full header + field grid yet is
  // tall enough to match the stats card beside it. Vertically centered so it
  // fills an equal-height row cleanly.
  if (dense) {
    const field = (label: string, value: string, valueClass = 'text-gray-800 font-medium') => (
      <div className="flex items-baseline gap-1 min-w-0">
        <span className="text-[9px] font-bold uppercase tracking-wide text-gray-800 shrink-0">{label}</span>
        <span className={`text-xs ${valueClass} truncate`}>{value}</span>
      </div>
    );
    const badgeClass = `inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold ${
      workStatus === 'Completed' || workStatus === 'COMPLETED' ? 'bg-emerald-100 text-emerald-700' :
      workStatus === 'In Progress' || workStatus === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-700' :
      workStatus === 'AS Approved' || workStatus === 'AS_APPROVED' ? 'bg-indigo-100 text-indigo-700' :
      'bg-gray-100 text-gray-600'
    }`;
    // Name removed — the work code · name already shows in the header above this
    // card. Every field (Status first) sits in one uniform grid so the columns
    // line up cleanly across both rows.
    return (
      <div className={`bg-gradient-to-br from-white via-slate-50 to-indigo-50/40 rounded-xl px-3 py-2 shadow-md flex items-center ${className}`}
        style={{ border: '3px double #6366f1' }}>
        <div className="flex items-center gap-2.5 w-full min-w-0">
          <span className="w-7 h-7 rounded-md bg-gradient-to-br from-indigo-100 to-indigo-200/60 flex items-center justify-center flex-shrink-0 shadow-sm">
            <i className="ri-building-4-line text-indigo-600 text-sm" />
          </span>
          <div className="flex-1 min-w-0 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-2">
            {workStatus && (
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-[9px] font-bold uppercase tracking-wide text-gray-800 shrink-0">Status</span>
                <span className={badgeClass}>{workStatus}</span>
              </div>
            )}
            {field('Type', workType || '-')}
            {field('Category', workCategory || '-')}
            {field('UDA', udaName || '-')}
            {field('Scheme', schemeName || '-')}
            {field('Cost', sanctionAmount ? `₹${fmtCurrency(sanctionAmount)}` : '-', 'text-emerald-700 font-bold')}
            {showAbstract && (
              <div className="flex items-baseline gap-1 min-w-0">
                <span className="text-[9px] font-bold uppercase tracking-wide text-gray-800 shrink-0 inline-flex items-center gap-0.5">
                  Abstract{!abstractMatches && <i className="ri-error-warning-line text-red-500 text-[10px]" title="Does not match Total Project Cost" />}
                </span>
                <span className={`text-xs font-bold truncate ${abstractMatches ? 'text-emerald-700' : 'text-red-600'}`}>{`₹${fmtAmount(Number(abstractAmount) || 0)}`}</span>
              </div>
            )}
            <div className="flex items-baseline gap-1 min-w-0">
              <span className="text-[9px] font-bold uppercase tracking-wide text-gray-800 shrink-0">Contractor</span>
              {contractorName ? (
                onContractorClick ? (
                  <button type="button" onClick={(e) => { e.stopPropagation(); onContractorClick(); }}
                    className="text-xs font-medium text-teal-600 hover:text-teal-800 hover:underline underline-offset-2 inline-flex items-center gap-1 truncate">
                    <i className="ri-user-3-line text-[10px]"></i>{contractorName}
                  </button>
                ) : (
                  <ContractorLink id={contractorId} name={contractorName} className="text-xs font-medium" />
                )
              ) : (
                <button type="button" onClick={(e) => { e.stopPropagation(); onAssignContractor?.(); }}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium hover:underline underline-offset-2">Not Assigned</button>
              )}
            </div>
            {extraFields?.map((f, i) => (
              <div key={i} className="flex items-baseline gap-1 min-w-0">
                <span className="text-[9px] font-bold uppercase tracking-wide text-gray-800 shrink-0">{f.label}</span>
                <span className="text-xs text-gray-800 font-medium truncate">{f.value || '-'}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-gradient-to-br from-white via-slate-50 to-indigo-50/40 rounded-xl p-3 shadow-md ${className}`}
      style={{ border: '3px double #6366f1' }}>
      <div className="flex items-start gap-2.5 mb-2">
        <div className="w-8 h-8 rounded-md bg-gradient-to-br from-indigo-100 to-indigo-200/60 flex items-center justify-center flex-shrink-0 shadow-sm">
          <i className="ri-building-4-line text-indigo-600 text-base" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {onWorkCodeClick ? (
              <button onClick={onWorkCodeClick} className="font-bold text-indigo-600 text-sm hover:text-indigo-800 hover:underline underline-offset-2 transition-colors">
                {workCode}
              </button>
            ) : (
              <span className="font-bold text-gray-900 text-sm">{workCode}</span>
            )}
            {workStatus && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold ${
                workStatus === 'Completed' || workStatus === 'COMPLETED' ? 'bg-emerald-100 text-emerald-700' :
                workStatus === 'In Progress' || workStatus === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-700' :
                workStatus === 'AS Approved' || workStatus === 'AS_APPROVED' ? 'bg-indigo-100 text-indigo-700' :
                'bg-gray-100 text-gray-600'
              }`}>{workStatus}</span>
            )}
          </div>
          <p className="text-xs text-gray-600 mt-0.5 leading-snug">{workName}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-2.5 pt-2.5 border-t border-dashed border-indigo-200/80">
        <div>
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Work Type/Sector</p>
          <p className="text-xs font-medium text-gray-800 mt-0.5">{workType || '-'}</p>
        </div>
        <div>
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Category</p>
          <p className="text-xs font-medium text-gray-800 mt-0.5">{workCategory || '-'}</p>
        </div>
        <div>
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">UDA</p>
          <p className="text-xs font-medium text-gray-800 mt-0.5">{udaName || '-'}</p>
        </div>
        <div>
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Scheme</p>
          <p className="text-xs font-medium text-gray-800 mt-0.5">{schemeName || '-'}</p>
        </div>
        <div>
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Total Project Cost</p>
          <p className="text-sm font-bold text-emerald-700 mt-0.5">{sanctionAmount ? `\u20B9${fmtCurrency(sanctionAmount)}` : '-'}</p>
        </div>
        {showAbstract && (
          <div>
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1">
              Abstract Amount
              {!abstractMatches && <i className="ri-error-warning-line text-red-500 text-[11px]" title="Does not match Total Project Cost" />}
            </p>
            <p className={`text-sm font-bold mt-0.5 ${abstractMatches ? 'text-emerald-700' : 'text-red-600'}`}
              title={abstractMatches ? 'Matches Total Project Cost' : 'Does not match Total Project Cost'}>
              {`\u20B9${fmtAmount(Number(abstractAmount) || 0)}`}
            </p>
          </div>
        )}
        <div>
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Contractor</p>
          {contractorName ? (
            onContractorClick ? (
              <button type="button" onClick={(e) => { e.stopPropagation(); onContractorClick(); }}
                className="text-xs font-medium text-teal-600 hover:text-teal-800 hover:underline underline-offset-2 mt-0.5 flex items-center gap-1 cursor-pointer">
                <i className="ri-user-3-line text-[10px]"></i>{contractorName}
              </button>
            ) : (
              <p className="text-xs font-medium mt-0.5"><ContractorLink id={contractorId} name={contractorName} /></p>
            )
          ) : (
            <button type="button" onClick={(e) => { e.stopPropagation(); onAssignContractor?.(); }}
              className="text-xs text-blue-600 hover:text-blue-800 mt-0.5 flex items-center gap-1 font-medium hover:underline underline-offset-2 cursor-pointer">
              Not Assigned
            </button>
          )}
        </div>
        {extraFields?.map((f, i) => (
          <div key={i}>
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">{f.label}</p>
            <p className="text-xs font-medium text-gray-800 mt-0.5">{f.value || '-'}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
